import React, { useEffect, useMemo, useState } from "react";
import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useLoaderData } from "react-router-dom";
import { createAdminQrCode, deleteAdminQrCode, deleteAdminQrScan, getAdminQrScans, updateAdminQrCode } from "../../../data/admin/qrScans";

export async function loader() {
    const data = await getAdminQrScans(500);
    return { data };
}

function formatDateTime(value) {
    if (!value) return "-";
    return new Date(value).toLocaleString("fr-FR", {
        dateStyle: "short",
        timeStyle: "short"
    });
}

function getOwnerLabel(item) {
    const fullName = `${item.ownerFirstname || ""} ${item.ownerLastname || ""}`.trim();
    if (fullName || item.ownerEmail) return fullName || item.ownerEmail;
    return item.ownerUserId ? `Utilisateur #${item.ownerUserId}` : "-";
}

function truncate(value, maxLength = 72) {
    if (!value) return "-";
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength - 1)}...`;
}

function getQrFormValues(item) {
    return {
        toolId: item?.toolId ?? "",
        code: item?.qrCodeValue ?? "",
        supportType: item?.supportType ?? "",
        label: item?.label ?? "",
        targetUrl: item?.targetUrl ?? "",
        isActive: item?.isActive ?? true
    };
}

function parseOptionalToolId(value) {
    if (value === "" || value === null || value === undefined) return null;
    const toolId = Number(value);
    return Number.isInteger(toolId) && toolId > 0 ? toolId : undefined;
}

const DEFAULT_QR = {
    size: 420,
    fg: "#00043c",
    bg: "#ffffff"
};

function getQrResolveUrl(item, publicSiteUrl) {
    const code = encodeURIComponent(item.qrCodeValue || "");
    const baseUrl = (publicSiteUrl || window.location.origin).replace(/\/+$/, "");
    return `${baseUrl}/q/${code}`;
}

function getQrImageUrl(item, publicSiteUrl) {
    const target = encodeURIComponent(getQrResolveUrl(item, publicSiteUrl));
    return `https://api.qrserver.com/v1/create-qr-code/?size=${DEFAULT_QR.size}x${DEFAULT_QR.size}&data=${target}&color=${DEFAULT_QR.fg.replace("#", "")}&bgcolor=${DEFAULT_QR.bg.replace("#", "")}`;
}

function downloadQrCodeImage(item, publicSiteUrl) {
    const imageUrl = getQrImageUrl(item, publicSiteUrl);
    const fileName = `qrcode-${item.qrCodeValue || item.qrCodeId}.png`;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = imageUrl;
    img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = DEFAULT_QR.size;
        canvas.height = DEFAULT_QR.size;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        const link = document.createElement("a");
        link.download = fileName;
        link.href = canvas.toDataURL("image/png");
        link.click();
    };
    img.onerror = () => {
        const link = document.createElement("a");
        link.href = imageUrl;
        link.download = fileName;
        link.target = "_blank";
        link.click();
    };
}

export default function AdminQrScansPage() {
    const { data: initialData } = useLoaderData();
    const [data, setData] = useState(initialData);
    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [selectedQrCode, setSelectedQrCode] = useState(null);
    const [qrCodeForm, setQrCodeForm] = useState(getQrFormValues(null));
    const [editVisible, setEditVisible] = useState(false);
    const [createVisible, setCreateVisible] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deletingQrCodeId, setDeletingQrCodeId] = useState(null);
    const [deletingScanId, setDeletingScanId] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS }
        });
    }, []);

    const qrCodes = useMemo(() => {
        return (data.qrCodes ?? []).map((item) => ({
            ...item,
            ownerLabel: getOwnerLabel(item),
            statusLabel: item.isActive ? "Actif" : "Inactif",
            searchText: [
                item.qrCodeValue,
                item.supportType,
                item.label,
                item.targetUrl,
                item.toolName,
                item.toolPublicId,
                item.toolCustomUrl,
                item.ownerFirstname,
                item.ownerLastname,
                item.ownerEmail,
                item.isActive ? "actif" : "inactif"
            ].filter(Boolean).join(" ")
        }));
    }, [data.qrCodes]);

    const toolOptions = useMemo(() => {
        const tools = new Map();
        (data.qrCodes ?? []).forEach((item) => {
            if (!item.toolId) return;
            if (!tools.has(item.toolId)) {
                tools.set(item.toolId, item.toolName || item.toolPublicId || `Matériel #${item.toolId}`);
            }
        });

        return Array.from(tools.entries())
            .map(([id, label]) => ({ id, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [data.qrCodes]);

    const onGlobalFilterChange = (event) => {
        const value = event.target.value;
        const nextFilters = filters ? { ...filters } : {
            global: { value: null, matchMode: FilterMatchMode.CONTAINS }
        };
        nextFilters.global.value = value;
        setFilters(nextFilters);
        setGlobalFilterValue(value);
    };

    const openEditModal = (item) => {
        setSelectedQrCode(item);
        setQrCodeForm(getQrFormValues(item));
        setError("");
        setEditVisible(true);
    };

    const closeEditModal = () => {
        if (saving) return;
        setEditVisible(false);
        setSelectedQrCode(null);
        setError("");
    };

    const openCreateModal = () => {
        setQrCodeForm({
            toolId: "",
            code: "",
            supportType: "Machine",
            label: "",
            targetUrl: "",
            isActive: true
        });
        setError("");
        setCreateVisible(true);
    };

    const closeCreateModal = () => {
        if (saving) return;
        setCreateVisible(false);
        setError("");
    };

    const updateFormValue = (field, value) => {
        setQrCodeForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveQrCode = async () => {
        if (!selectedQrCode) return;

        const qrCodeId = Number(selectedQrCode.qrCodeId);
        if (!Number.isInteger(qrCodeId) || qrCodeId <= 0) {
            setError("Impossible de modifier ce QR code : identifiant introuvable.");
            return;
        }

        setSaving(true);
        setError("");

        try {
            const toolId = parseOptionalToolId(qrCodeForm.toolId);
            if (toolId === undefined) {
                setError("Le matériel associé est introuvable.");
                return;
            }

            const payload = { ...qrCodeForm, toolId };
            await updateAdminQrCode(qrCodeId, payload);
            const refreshed = await getAdminQrScans(500);
            setData(refreshed);
            setEditVisible(false);
            setSelectedQrCode(null);
        } catch (saveError) {
            const message = saveError instanceof Response
                ? `Erreur ${saveError.status || ""}`.trim()
                : saveError.message;
            setError(message || "Impossible de modifier ce QR code.");
        } finally {
            setSaving(false);
        }
    };

    const handleCreateQrCode = async () => {
        const toolId = parseOptionalToolId(qrCodeForm.toolId);
        if (toolId === undefined) {
            setError("Le matériel associé est introuvable.");
            return;
        }

        setSaving(true);
        setError("");

        try {
            const created = await createAdminQrCode({ ...qrCodeForm, toolId });
            setData((prev) => {
                const qrCodes = [created, ...(prev.qrCodes ?? [])];

                return {
                    ...prev,
                    totalQrCodes: qrCodes.length,
                    activeQrCodes: qrCodes.filter((item) => item.isActive).length,
                    qrCodes
                };
            });
            setCreateVisible(false);
        } catch (createError) {
            setError(createError.message || "Impossible d'ajouter ce QR code.");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteScan = async (scan) => {
        if (!window.confirm("Supprimer ce scan QR code ?")) return;

        setDeletingScanId(scan.scanId);
        setError("");

        try {
            await deleteAdminQrScan(scan.scanId);
            const refreshed = await getAdminQrScans(500);
            setData(refreshed);
        } catch (deleteError) {
            const message = deleteError instanceof Response
                ? `Erreur ${deleteError.status || ""}`.trim()
                : deleteError.message;
            setError(message || "Impossible de supprimer ce scan.");
        } finally {
            setDeletingScanId(null);
        }
    };

    const handleDeleteQrCode = async (item) => {
        const qrCodeId = Number(item.qrCodeId);
        if (!Number.isInteger(qrCodeId) || qrCodeId <= 0) {
            setError("Impossible de supprimer ce QR code : identifiant introuvable.");
            return;
        }

        const scanCount = Number(item.scanCount || 0);
        const message = scanCount > 0
            ? `Supprimer ce QR code et ses ${scanCount.toLocaleString("fr-FR")} scan(s) ?`
            : "Supprimer ce QR code ?";

        if (!window.confirm(message)) return;

        setDeletingQrCodeId(qrCodeId);
        setError("");

        try {
            await deleteAdminQrCode(qrCodeId);
            const refreshed = await getAdminQrScans(500);
            setData(refreshed);
        } catch (deleteError) {
            const message = deleteError instanceof Response
                ? `Erreur ${deleteError.status || ""}`.trim()
                : deleteError.message;
            setError(message || "Impossible de supprimer ce QR code.");
        } finally {
            setDeletingQrCodeId(null);
        }
    };

    const qrCodesHeader = (
        <div className="admin-qr-table-header">
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText
                    value={globalFilterValue}
                    onChange={onGlobalFilterChange}
                    placeholder="Rechercher un QR code..."
                />
            </span>
            <Button
                type="button"
                className="admin-qr-add-button"
                icon="pi pi-plus"
                label="Ajouter"
                size="small"
                onClick={openCreateModal}
            />
        </div>
    );

    const toolOptionsDatalist = (
        <datalist id="admin-qr-tool-options">
            {toolOptions.map((tool) => (
                <option key={tool.id} value={tool.id}>{tool.label}</option>
            ))}
        </datalist>
    );

    const toolBodyTemplate = (item) => {
        const label = item.toolName || item.toolPublicId || "Aucun outil";

        return (
            <>
                {item.toolCustomUrl ? (
                    <a href={`/outil/${item.toolCustomUrl}`}>{label}</a>
                ) : (
                    <span className="admin-qr-muted">{label}</span>
                )}
                <div className="admin-qr-muted" title={item.targetUrl || ""}>
                    {truncate(item.targetUrl)}
                </div>
            </>
        );
    };

    const ownerBodyTemplate = (item) => (
        item.ownerUserId ? <a href={`/user/${item.ownerUserId}`}>{item.ownerLabel}</a> : <span className="admin-qr-muted">-</span>
    );

    const typeBodyTemplate = (item) => item.label || item.supportType || "-";
    const createdBodyTemplate = (item) => formatDateTime(item.createdAtUtc);
    const lastScanBodyTemplate = (item) => formatDateTime(item.lastScannedAtUtc);
    const scanCountBodyTemplate = (item) => item.scanCount.toLocaleString("fr-FR");

    const actionBodyTemplate = (item) => (
        <div className="admin-qr-actions">
            <Button
                type="button"
                className="admin-qr-icon-button"
                icon="pi pi-pencil"
                aria-label="Modifier"
                tooltip="Modifier"
                tooltipOptions={{ position: "top" }}
                size="small"
                text
                onClick={() => openEditModal(item)}
            />
            <Button
                type="button"
                className="admin-qr-icon-button"
                icon="pi pi-download"
                aria-label="Télécharger"
                tooltip="Télécharger"
                tooltipOptions={{ position: "top" }}
                size="small"
                text
                onClick={() => downloadQrCodeImage(item, data.publicSiteUrl)}
            />
            <Button
                type="button"
                className="admin-qr-icon-button"
                icon="pi pi-trash"
                aria-label="Supprimer"
                tooltip="Supprimer"
                tooltipOptions={{ position: "top" }}
                size="small"
                text
                severity="danger"
                loading={deletingQrCodeId === item.qrCodeId}
                onClick={() => handleDeleteQrCode(item)}
            />
        </div>
    );

    const editFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Enregistrer" size="small" loading={saving} onClick={handleSaveQrCode} />
            <Button label="Fermer" size="small" severity="secondary" onClick={closeEditModal} disabled={saving} />
        </div>
    );

    const createFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Ajouter" size="small" loading={saving} onClick={handleCreateQrCode} />
            <Button label="Fermer" size="small" severity="secondary" onClick={closeCreateModal} disabled={saving} />
        </div>
    );

    return (
        <div className="admin-qr-page">
            <div className="admin-qr-header">
                <h1>QR codes</h1>
                <p className="admin-qr-generated">Mise à jour : {formatDateTime(data.generatedAtUtc)}</p>
            </div>
            {toolOptionsDatalist}
            {error && !editVisible && !createVisible ? <p className="admin-qr-error">{error}</p> : null}

            <div className="admin-qr-metrics">
                <article className="admin-qr-metric-card">
                    <span>Total QR codes</span>
                    <strong>{data.totalQrCodes.toLocaleString("fr-FR")}</strong>
                </article>
                <article className="admin-qr-metric-card">
                    <span>QR actifs</span>
                    <strong>{data.activeQrCodes.toLocaleString("fr-FR")}</strong>
                </article>
                <article className="admin-qr-metric-card">
                    <span>Total scans</span>
                    <strong>{data.totalScans.toLocaleString("fr-FR")}</strong>
                </article>
                <article className="admin-qr-metric-card">
                    <span>24 dernières heures</span>
                    <strong>{data.last24Hours.toLocaleString("fr-FR")}</strong>
                </article>
                <article className="admin-qr-metric-card">
                    <span>7 derniers jours</span>
                    <strong>{data.last7Days.toLocaleString("fr-FR")}</strong>
                </article>
                <article className="admin-qr-metric-card">
                    <span>30 derniers jours</span>
                    <strong>{data.last30Days.toLocaleString("fr-FR")}</strong>
                </article>
                <article className="admin-qr-metric-card">
                    <span>QR uniques</span>
                    <strong>{data.uniqueQrCodes.toLocaleString("fr-FR")}</strong>
                </article>
                <article className="admin-qr-metric-card">
                    <span>Taux mobile</span>
                    <strong>{data.mobileRatePercent.toLocaleString("fr-FR")}%</strong>
                </article>
            </div>

            <div className="admin-qr-grid">
                <section className="admin-qr-card admin-qr-card-table">
                    <h2>Liste des QR codes</h2>
                    <DataTable
                        className="admin-qr-codes-table"
                        value={qrCodes}
                        paginator
                        showGridlines
                        rows={25}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        dataKey="qrCodeId"
                        filters={filters}
                        globalFilterFields={["searchText"]}
                        header={qrCodesHeader}
                        emptyMessage="Aucun QR code trouvé"
                        tableStyle={{ tableLayout: "fixed" }}
                    >
                        <Column header="Créé le" body={createdBodyTemplate} sortable sortField="createdAtUtc" style={{ width: "8.5rem" }} />
                        <Column header="Matériel" body={toolBodyTemplate} sortable sortField="toolName" style={{ width: "22%" }} />
                        <Column header="Propriétaire" body={ownerBodyTemplate} sortable sortField="ownerLabel" style={{ width: "16%" }} />
                        <Column header="Type" body={typeBodyTemplate} sortable sortField="supportType" style={{ width: "14%" }} />
                        <Column field="qrCodeValue" header="Code" sortable style={{ width: "8rem" }} />
                        <Column field="statusLabel" header="Statut" sortable style={{ width: "7rem" }} />
                        <Column header="Scans" body={scanCountBodyTemplate} sortable sortField="scanCount" style={{ width: "6rem" }} />
                        <Column header="Dernier scan" body={lastScanBodyTemplate} sortable sortField="lastScannedAtUtc" style={{ width: "8.5rem" }} />
                        <Column header="" body={actionBodyTemplate} style={{ width: "7.25rem" }} />
                    </DataTable>
                </section>

                <section className="admin-qr-card">
                    <h2>Top matériels les plus scannés</h2>
                    {data.topTools?.length > 0 ? (
                        <ol className="admin-qr-top-list">
                            {data.topTools.map((tool) => (
                                <li key={tool.toolId}>
                                    {tool.toolCustomUrl ? (
                                        <a href={`/outil/${tool.toolCustomUrl}`}>{tool.toolName || tool.toolPublicId}</a>
                                    ) : (
                                        <span>{tool.toolName || tool.toolPublicId}</span>
                                    )}
                                    <span>{tool.count.toLocaleString("fr-FR")} scans</span>
                                </li>
                            ))}
                        </ol>
                    ) : (
                        <p className="admin-qr-empty">Aucun scan enregistré pour le moment.</p>
                    )}
                </section>

                <section className="admin-qr-card admin-qr-card-table">
                    <h2>Scans QR codes</h2>
                    {data.items?.length === 0 ? (
                        <p className="admin-qr-empty">Aucun scan enregistré pour le moment.</p>
                    ) : (
                        <div className="admin-qr-table-wrapper">
                            <table className="admin-qr-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Matériel</th>
                                        <th>Propriétaire</th>
                                        <th>Type</th>
                                        <th>Support</th>
                                        <th></th>
                                        <th>Référent</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.items.map((item) => (
                                        <tr key={item.scanId}>
                                            <td>{formatDateTime(item.scannedAtUtc)}</td>
                                            <td>
                                                {item.toolCustomUrl ? (
                                                    <a href={`/outil/${item.toolCustomUrl}`}>{item.toolName || item.toolPublicId}</a>
                                                ) : (
                                                    <span className="admin-qr-muted">{item.toolName || item.toolPublicId || "Aucun outil"}</span>
                                                )}
                                                <div className="admin-qr-muted">{item.qrCodeValue}</div>
                                            </td>
                                            <td>
                                                {item.ownerUserId ? (
                                                    <a href={`/user/${item.ownerUserId}`}>{getOwnerLabel(item)}</a>
                                                ) : (
                                                    <span className="admin-qr-muted">-</span>
                                                )}
                                            </td>
                                            <td>{item.deviceType || "-"}</td>
                                            <td>{item.label || item.supportType || "-"}</td>
                                            <td>
                                                <Button
                                                    type="button"
                                                    className="admin-qr-icon-button"
                                                    icon="pi pi-trash"
                                                    aria-label="Supprimer"
                                                    tooltip="Supprimer"
                                                    tooltipOptions={{ position: "top" }}
                                                    size="small"
                                                    text
                                                    severity="danger"
                                                    loading={deletingScanId === item.scanId}
                                                    onClick={() => handleDeleteScan(item)}
                                                />
                                            </td>
                                            <td title={item.referrer || ""}>{truncate(item.referrer)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>

            <Dialog
                header="Modifier le QR code"
                visible={editVisible}
                className="admin-qr-dialog"
                style={{ width: "min(560px, 95vw)" }}
                baseZIndex={2000}
                appendTo={typeof window !== "undefined" ? document.body : undefined}
                dismissableMask
                onHide={closeEditModal}
                footer={editFooter}
            >
                {selectedQrCode ? (
                    <div className="admin-qr-form">
                        <div className="admin-qr-form-field">
                            <label htmlFor="qr-tool-id">Matériel associé</label>
                            <InputText
                                id="qr-tool-id"
                                list="admin-qr-tool-options"
                                keyfilter="int"
                                value={qrCodeForm.toolId}
                                onChange={(event) => updateFormValue("toolId", event.target.value)}
                                placeholder="Aucun matériel"
                            />
                            <small className="admin-qr-help">Videz le champ pour supprimer l'outil associé.</small>
                        </div>
                        <div className="admin-qr-form-field">
                            <label htmlFor="qr-code-value">Code</label>
                            <InputText
                                id="qr-code-value"
                                value={qrCodeForm.code}
                                onChange={(event) => updateFormValue("code", event.target.value)}
                            />
                        </div>
                        <div className="admin-qr-form-field">
                            <label htmlFor="qr-support-type">Type de support</label>
                            <InputText
                                id="qr-support-type"
                                value={qrCodeForm.supportType}
                                onChange={(event) => updateFormValue("supportType", event.target.value)}
                            />
                        </div>
                        <div className="admin-qr-form-field">
                            <label htmlFor="qr-label">Libellé</label>
                            <InputText
                                id="qr-label"
                                value={qrCodeForm.label}
                                onChange={(event) => updateFormValue("label", event.target.value)}
                            />
                        </div>
                        <div className="admin-qr-form-field">
                            <label htmlFor="qr-target-url">URL cible</label>
                            <InputTextarea
                                id="qr-target-url"
                                value={qrCodeForm.targetUrl}
                                rows={3}
                                onChange={(event) => updateFormValue("targetUrl", event.target.value)}
                            />
                        </div>
                        <label className="admin-qr-checkbox">
                            <input
                                type="checkbox"
                                checked={qrCodeForm.isActive}
                                onChange={(event) => updateFormValue("isActive", event.target.checked)}
                            />
                            QR code actif
                        </label>
                        {error && <p className="admin-qr-error">{error}</p>}
                    </div>
                ) : null}
            </Dialog>

            <Dialog
                header="Ajouter un QR code"
                visible={createVisible}
                className="admin-qr-dialog"
                style={{ width: "min(560px, 95vw)" }}
                baseZIndex={2000}
                appendTo={typeof window !== "undefined" ? document.body : undefined}
                dismissableMask
                onHide={closeCreateModal}
                footer={createFooter}
            >
                <div className="admin-qr-form">
                    <div className="admin-qr-form-field">
                        <label htmlFor="new-qr-tool-id">Matériel</label>
                        <InputText
                            id="new-qr-tool-id"
                            list="admin-qr-tool-options"
                            keyfilter="int"
                            value={qrCodeForm.toolId}
                            onChange={(event) => updateFormValue("toolId", event.target.value)}
                            placeholder="Aucun matériel"
                        />
                        <small className="admin-qr-help">Laissez vide pour créer un QR code sans outil associé.</small>
                    </div>
                    <div className="admin-qr-form-field">
                        <label htmlFor="new-qr-code-value">Code</label>
                        <InputText
                            id="new-qr-code-value"
                            value={qrCodeForm.code}
                            onChange={(event) => updateFormValue("code", event.target.value)}
                        />
                    </div>
                    <div className="admin-qr-form-field">
                        <label htmlFor="new-qr-support-type">Type de support</label>
                        <InputText
                            id="new-qr-support-type"
                            value={qrCodeForm.supportType}
                            onChange={(event) => updateFormValue("supportType", event.target.value)}
                        />
                    </div>
                    <div className="admin-qr-form-field">
                        <label htmlFor="new-qr-label">Libellé</label>
                        <InputText
                            id="new-qr-label"
                            value={qrCodeForm.label}
                            onChange={(event) => updateFormValue("label", event.target.value)}
                        />
                    </div>
                    <div className="admin-qr-form-field">
                        <label htmlFor="new-qr-target-url">URL cible</label>
                        <InputTextarea
                            id="new-qr-target-url"
                            value={qrCodeForm.targetUrl}
                            rows={3}
                            onChange={(event) => updateFormValue("targetUrl", event.target.value)}
                        />
                    </div>
                    <label className="admin-qr-checkbox">
                        <input
                            type="checkbox"
                            checked={qrCodeForm.isActive}
                            onChange={(event) => updateFormValue("isActive", event.target.checked)}
                        />
                        QR code actif
                    </label>
                    {error && <p className="admin-qr-error">{error}</p>}
                </div>
            </Dialog>
        </div>
    );
}
